import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useMap from "@/lib/map/useMap";
import useRequest from "@/lib/request/useRequest";
import {
    faPencil,
    faSave,
    faUndo,
    faUpload,
    faPlus,
    faTrash,
    faRefresh,
    faEllipsisH,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    addToast,
    Button,
    Input,
    Card,
    CardBody,
    useDisclosure,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    Skeleton,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Switch,
} from "@heroui/react";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FeatureCollection } from "geojson";
import * as turf from "@turf/turf";
import { isNumber, roundTo } from "@/lib/utils/numbers";
import { FIELD_BOUNDARY_DRAW_OPTIONS } from "@/lib/map/layers";
import { FieldConnection, PLATFORM_LOGOS } from "@/types/connection";
import NewConnection from "../NewConnection";
import { useTranslation } from "react-i18next";

const MAPBOX_DRAW_SOURCE_IDS = ["mapbox-gl-draw-cold", "mapbox-gl-draw-hot"];

function FieldManagement() {
    const { t } = useTranslation();
    const { useMutateRequest, useGetRequest } = useRequest();
    const { fieldQuery, fieldId, geometryQuery } = useFieldDashboard() ?? {};
    const updateField = useMutateRequest({
        successMessage: t("success.field_updated_successfully", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_update_field", {
            ns: "messages",
        }),
    });
    const [name, setName] = useState(fieldQuery?.data?.name);

    const [isEditingBoundary, setIsEditingBoundary] = useState(false);

    const { map, mapLoaded } = useMap();
    const draw = useRef<MapboxDraw | null>(null);

    const bringToFront = useCallback(() => {
        if (!draw.current || !mapLoaded || !map) return false;
        if (!map.loaded()) return false;

        const plots = draw.current.getAll();
        if (map.hasControl(draw.current)) {
            map.removeControl(draw.current);
        }
        const hasDrawSource = MAPBOX_DRAW_SOURCE_IDS.some((sourceId) =>
            map.getSource(sourceId),
        );
        if (hasDrawSource) return false;

        map.addControl(draw.current, "top-left");
        draw.current.add(plots);
        return true;
    }, [map, mapLoaded]);

    const [drawFeatures, setDrawFeatures] = useState<
        FeatureCollection | undefined
    >(undefined);

    useEffect(() => {
        if (!map || !mapLoaded || draw.current) return;

        const handleDrawUpdate = () => {
            setDrawFeatures(draw.current?.getAll());
        };

        const addDrawControl = () => {
            const hasDrawSource = MAPBOX_DRAW_SOURCE_IDS.some((sourceId) =>
                map.getSource(sourceId),
            );
            if (draw.current || hasDrawSource || !map.loaded()) return;

            draw.current = new MapboxDraw(FIELD_BOUNDARY_DRAW_OPTIONS);
            map.addControl(draw.current, "bottom");
            map.on("draw.update", handleDrawUpdate);
        };

        if (map.loaded()) {
            addDrawControl();
        } else {
            map.once("idle", addDrawControl);
        }

        return () => {
            map.off("idle", addDrawControl);
            map.off("draw.update", handleDrawUpdate);
            if (map && draw.current) {
                if (map.hasControl(draw.current)) {
                    map.removeControl(draw.current);
                }
                draw.current = null;
            }
        };
    }, [map, mapLoaded]);

    useEffect(() => {
        if (!draw.current || !geometryQuery?.data || !mapLoaded) return;
        if (isEditingBoundary) {
            if (!bringToFront()) return;
            draw.current.deleteAll();
            const [featureId] = draw.current.add(geometryQuery.data.field);
            if (!featureId) return;
            draw.current.changeMode("direct_select", {
                featureId,
            });
        } else {
            draw.current.deleteAll();
        }
    }, [bringToFront, geometryQuery?.data, isEditingBoundary, mapLoaded]);

    const area = useMemo(() => {
        const calculateArea = (features: FeatureCollection) => {
            return turf.area(features) / 4046.8564224;
        };
        if (drawFeatures) {
            const features = draw.current?.getAll();
            if (!features) return 0;
            return calculateArea(features);
        } else {
            if (!geometryQuery?.data?.field?.geometry) return 0;
            return calculateArea(
                turf.featureCollection([geometryQuery?.data?.field]),
            );
        }
    }, [drawFeatures, geometryQuery?.data]);

    const uploadFile = useMutateRequest({
        successMessage: t("success.file_uploaded_successfully", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_upload_file", {
            ns: "messages",
        }),
    });

    const uploadFileBoundary = useCallback(
        (file?: File) => {
            if (!file) return;
            try {
                if (
                    file.type.endsWith("json") ||
                    file.type.endsWith("geojson")
                ) {
                    //open and read json
                    const fileReader = new FileReader();
                    fileReader.readAsText(file, "utf-8");
                    fileReader.onload = async () => {
                        let coords = undefined;
                        if (
                            JSON.parse(fileReader.result as string).geometry
                                .type == "MultiPolygon"
                        ) {
                            coords = JSON.parse(fileReader.result as string)
                                .geometry.coordinates;
                        }
                        if (
                            JSON.parse(fileReader.result as string).geometry
                                .type == "Polygon"
                        ) {
                            coords = [
                                JSON.parse(fileReader.result as string).geometry
                                    .coordinates,
                            ];
                        }
                        updateField.mutate(
                            {
                                url: `/fields/${fieldId}/`,
                                method: "put",
                                data: {
                                    geometry: {
                                        type: "MultiPolygon",
                                        coordinates: coords,
                                    },
                                    properties: {
                                        name,
                                        grower: fieldQuery?.data?.grower,
                                    },
                                },
                            },
                            {
                                onSuccess: () => {
                                    fieldQuery?.refetch();
                                    geometryQuery?.refetch();
                                    setIsEditingBoundary(false);
                                },
                            },
                        );
                    };
                } else {
                    const formData = new FormData();
                    formData.append("file", file);
                    formData.append("name", name ?? "");
                    formData.append(
                        "grower",
                        fieldQuery?.data?.grower?.toString() ?? "",
                    );
                    uploadFile.mutate(
                        {
                            url: `/fields/${fieldId}/upload/`,
                            method: "put",
                            data: formData,
                        },
                        {
                            onSuccess: () => {
                                fieldQuery?.refetch();
                                geometryQuery?.refetch();
                                setIsEditingBoundary(false);
                            },
                        },
                    );
                }
            } catch {
                addToast({
                    color: "danger",
                    title: t("addToast.failed_to_upload_file", {
                        ns: "messages",
                    }),
                    description: t("addToast.make_sure_file_is_valid", {
                        ns: "messages",
                    }),
                });
            }
        },
        [fieldId, fieldQuery, geometryQuery, name, t, updateField, uploadFile],
    );

    const fileInputRef = useRef<HTMLInputElement>(null);

    const getConnections = useGetRequest<FieldConnection[]>({
        url: `/field_connections/`,
        params: {
            field: fieldId,
        },
        enabled: !!fieldId,
    });
    const connections: FieldConnection[] = useMemo(() => {
        return getConnections?.data ?? [];
    }, [getConnections]);
    const deleteConnection = useMutateRequest({
        method: "delete",
        successMessage: t("success.deleted_connection"),
        errorMessage: t("error.failed_to_delete_connection"),
    });
    const deleteConnectionHandler = useCallback(
        (connectionId: number) => {
            deleteConnection.mutate(
                {
                    url: `/field_connections/${connectionId}/`,
                },
                {
                    onSuccess: () => {
                        getConnections?.refetch();
                    },
                },
            );
        },
        [deleteConnection, getConnections],
    );
    const syncConnection = useMutateRequest({
        method: "put",
        successMessage: t("success.synced_connection"),
        errorMessage: t("error.failed_to_sync_connection"),
    });
    const syncConnectionHandler = useCallback(
        (connectionId: number) => {
            syncConnection.mutate(
                {
                    url: `/field_connections/${connectionId}/sync/`,
                },
                {
                    onSuccess: () => {
                        getConnections?.refetch();
                        fieldQuery?.refetch();
                        geometryQuery?.refetch();
                    },
                },
            );
        },
        [syncConnection, getConnections, fieldQuery, geometryQuery],
    );
    const { isOpen, onOpen, onOpenChange } = useDisclosure();
    const toggleSyncOperations = useMutateRequest({
        method: "patch",
        successMessage: t("success.synced_operations"),
        errorMessage: t("error.failed_to_sync_operations"),
    });
    const toggleSyncOperationsHandler = useCallback(
        (connectionId: number, sync_operations: boolean) => {
            toggleSyncOperations.mutate(
                {
                    url: `/field_connections/${connectionId}/`,
                    data: {
                        sync_operations: sync_operations,
                    },
                },
                {
                    onSuccess: () => {
                        getConnections?.refetch();
                    },
                },
            );
        },
        [toggleSyncOperations, getConnections],
    );
    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2 items-end">
                <Input
                    labelPlacement="outside"
                    size="sm"
                    label={t("field_management.field_name", {
                        ns: "setup",
                    })}
                    value={name}
                    isDisabled={updateField.isPending}
                    isRequired
                    isInvalid={!name}
                    onChange={(e) => {
                        setName(e.target.value);
                    }}
                />
                <Button
                    color="secondary"
                    size="sm"
                    startContent={<FontAwesomeIcon icon={faSave} />}
                    isDisabled={updateField.isPending || !name}
                    isLoading={updateField.isPending}
                    onPress={() => {
                        updateField.mutate(
                            {
                                url: `/fields/${fieldId}/`,
                                method: "patch",
                                data: {
                                    properties: {
                                        name,
                                    },
                                },
                            },
                            {
                                onSuccess: () => {
                                    fieldQuery?.refetch();
                                },
                            },
                        );
                    }}
                >
                    {t("save", {
                        ns: "common",
                    })}
                </Button>
            </div>
            <div className="flex flex-col">
                <div className="text-xs">
                    {t("field_management.boundary", {
                        ns: "setup",
                    })}
                </div>
                <div className="flex flex-row gap-2 items-end">
                    <Button
                        size="sm"
                        color="primary"
                        startContent={
                            <FontAwesomeIcon
                                icon={isEditingBoundary ? faUndo : faPencil}
                            />
                        }
                        onPress={() => {
                            if (isEditingBoundary) {
                                setIsEditingBoundary(false);
                            } else {
                                setIsEditingBoundary(true);
                            }
                        }}
                    >
                        {isEditingBoundary
                            ? t("field_management.undo_changes", {
                                  ns: "setup",
                              })
                            : t("field_management.edit_boundary", {
                                  ns: "setup",
                              })}
                    </Button>
                    <Button
                        size="sm"
                        color="primary"
                        startContent={<FontAwesomeIcon icon={faUpload} />}
                        onPress={() => {
                            if (fileInputRef.current) {
                                fileInputRef.current.click();
                            }
                        }}
                    >
                        {t("upload", {
                            ns: "common",
                        })}
                    </Button>
                    <input
                        type="file"
                        hidden
                        ref={fileInputRef}
                        onChange={(e) => {
                            uploadFileBoundary(e.target.files?.[0]);
                        }}
                    />
                    <div>
                        <Input
                            labelPlacement="outside"
                            size="sm"
                            label={t("field_management.area", {
                                ns: "setup",
                            })}
                            placeholder="0.00"
                            endContent="ac"
                            isReadOnly
                            value={
                                isNumber(area)
                                    ? roundTo(area, 2).toString()
                                    : "0.00"
                            }
                        />
                    </div>
                    <Button
                        color="secondary"
                        size="sm"
                        startContent={<FontAwesomeIcon icon={faSave} />}
                        isDisabled={updateField.isPending || !isEditingBoundary}
                        isLoading={updateField.isPending}
                        onPress={() => {
                            const geom =
                                draw.current?.getAll()?.features[0]?.geometry;
                            if (!geom) {
                                addToast({
                                    color: "danger",
                                    title: t("addToast.invalid_geometry", {
                                        ns: "messages",
                                    }),
                                });
                                return;
                            }
                            updateField.mutate(
                                {
                                    url: `/fields/${fieldId}/`,
                                    method: "put",
                                    data: {
                                        geometry: geom,
                                        properties: {
                                            name,
                                            grower: fieldQuery?.data?.grower,
                                        },
                                    },
                                },
                                {
                                    onSuccess: () => {
                                        fieldQuery?.refetch();
                                        geometryQuery?.refetch();
                                        setIsEditingBoundary(false);
                                    },
                                },
                            );
                        }}
                    >
                        {t("field_management.save_boundary", {
                            ns: "setup",
                        })}
                    </Button>
                </div>
            </div>
            <div className="flex flex-row justify-between items-center px-1  mt-2">
                <p className="text-sm">
                    {t("field_management.connections", {
                        ns: "setup",
                    })}
                </p>
                <Button color="secondary" size="sm" onPress={onOpen}>
                    <FontAwesomeIcon icon={faPlus} />
                    {t("field_management.add_connection", {
                        ns: "setup",
                    })}
                </Button>
            </div>
            {getConnections.isLoading ? (
                <div className="flex flex-col gap-2 px-3">
                    <Skeleton className="h-10 w-full rounded-lg" />
                </div>
            ) : (
                <div className="flex flex-col gap-2 px-3">
                    {connections?.map((connection) => (
                        <Card key={connection.id}>
                            <CardBody className="flex flex-row gap-2 w-full items-center justify-between px-2">
                                <img
                                    src={
                                        PLATFORM_LOGOS[
                                            connection.user_connection.platform
                                        ]
                                    }
                                    alt={connection.user_connection.platform}
                                    className="h-10 w-auto object-contain"
                                />
                                <p className="text-sm truncate text-gray-500 font-semibold">
                                    {connection.platform_field_name}
                                </p>
                                <Popover placement="right">
                                    <PopoverTrigger>
                                        <Button
                                            color="primary"
                                            size="sm"
                                            isIconOnly
                                        >
                                            <FontAwesomeIcon
                                                icon={faEllipsisH}
                                            />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent>
                                        <div className="flex flex-col gap-2">
                                            <Switch
                                                size="sm"
                                                isSelected={
                                                    connection.sync_operations
                                                }
                                                onValueChange={(value) => {
                                                    toggleSyncOperationsHandler(
                                                        connection.id,
                                                        value,
                                                    );
                                                }}
                                            >
                                                {t(
                                                    "field_management.sync_operations",
                                                    {
                                                        ns: "setup",
                                                    },
                                                )}
                                            </Switch>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                isLoading={
                                                    syncConnection.isPending
                                                }
                                                onPress={() => {
                                                    syncConnectionHandler(
                                                        connection.id,
                                                    );
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faRefresh}
                                                    className="mr-2"
                                                />
                                                {t("field_management.sync", {
                                                    ns: "setup",
                                                })}
                                            </Button>
                                            <Button
                                                color="danger"
                                                size="sm"
                                                onPress={() => {
                                                    deleteConnectionHandler(
                                                        connection.id,
                                                    );
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faTrash}
                                                    className="mr-2"
                                                />
                                                {t(
                                                    "field_management.delete_connection",
                                                    {
                                                        ns: "setup",
                                                    },
                                                )}
                                            </Button>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </CardBody>
                        </Card>
                    ))}
                </div>
            )}
            {connections?.length === 0 && !getConnections.isLoading && (
                <p className="text-sm text-gray-500 text-center">
                    {t("field_management.no_connections", {
                        ns: "setup",
                    })}
                </p>
            )}
            <Modal
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                size="lg"
                scrollBehavior="inside"
            >
                <ModalContent>
                    <ModalHeader>
                        {t("field_management.connect_to_platform", {
                            ns: "setup",
                        })}
                    </ModalHeader>
                    <ModalBody>
                        <NewConnection
                            growerId={fieldQuery?.data?.grower}
                            fieldId={Number(fieldId)}
                            onFinish={() => {
                                getConnections?.refetch();
                                fieldQuery?.refetch();
                                geometryQuery?.refetch();
                                onOpenChange();
                            }}
                        />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </div>
    );
}

export default FieldManagement;
